import type { VisitStatus } from '@empower-safety/shared-types';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Edit2,
  Loader2,
  MapPin,
  Navigation,
  Plug,
  Plus,
  Trash2,
  User as UserIcon,
  X,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRoster } from '../api/useRoster.js';
import {
  type CreateVisitInput,
  type VisitDto,
  useCreateVisit,
  useDeleteVisit,
  useUpdateVisit,
  useVisits,
} from '../api/useVisits.js';
import { EmptyState } from '../components/EmptyState.js';
import { RoleGate } from '../components/RoleGate.js';
import { cn } from '../lib/cn.js';
import { formatDateTime } from '../lib/format.js';

// Visits page. Daily view grouped by worker. Admin + monitor see everything; a worker
// would use the mobile app for their own schedule.
//
// Status chips use colour to telegraph urgency: scheduled (blue) → arrived (amber) →
// completed (green), plus cancelled (grey) and no_show (red).

// Visit status → design system pill. Reuses the .pill base from the global stylesheet
// so the styling stays consistent with alarms / broadcasts / journeys.
const STATUS_CHIPS: Record<VisitStatus, string> = {
  scheduled: 'pill-info',
  arrived: 'pill-acknowledged',
  completed: 'pill-resolved',
  cancelled: 'pill-neutral',
  no_show: 'pill-active',
};

export default function Visits() {
  const [date, setDate] = useState<string>(() => toDateInput(new Date()));
  const [filterUser, setFilterUser] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<VisitDto | null>(null);

  const window = useMemo(() => {
    const start = new Date(`${date}T00:00:00`);
    const end = new Date(start.getTime() + 24 * 60 * 60_000);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [date]);

  const visits = useVisits({
    from: window.from,
    to: window.to,
    user_id: filterUser ?? undefined,
  });
  const roster = useRoster();

  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; visits: VisitDto[] }>();
    for (const v of visits.data?.visits ?? []) {
      const key = v.user_id;
      const existing = map.get(key);
      if (existing) existing.visits.push(v);
      else map.set(key, { name: v.user_display_name ?? 'Unknown', visits: [v] });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [visits.data]);

  const totalCount = visits.data?.visits.length ?? 0;
  const workers = (roster.data?.members ?? []).filter((m) => m.app_role === 'worker');

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Visits</h1>
          <p className="text-sm text-ink-faint">
            Scheduled work sessions for this organisation. Manual entries are fully editable;
            roster-sourced visits (AlayaCare etc.) sync from upstream.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <label
              htmlFor="vdate"
              className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ink-faint"
            >
              Date
            </label>
            <div className="flex items-center gap-1.5">
              <input
                id="vdate"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
              {date !== toDateInput(new Date()) && (
                <button
                  type="button"
                  onClick={() => setDate(toDateInput(new Date()))}
                  className="rounded-md border border-line-strong bg-white px-2 py-1.5 text-xs font-semibold text-brand-purple hover:bg-surface-3"
                >
                  Today
                </button>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="vworker"
              className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ink-faint"
            >
              Worker
            </label>
            <select
              id="vworker"
              value={filterUser ?? ''}
              onChange={(e) => setFilterUser(e.target.value || null)}
              className="rounded-md border border-line-strong bg-white px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value="">Everyone</option>
              {workers.map((w) => (
                <option key={w.user_id} value={w.user_id}>
                  {w.display_name}
                </option>
              ))}
            </select>
          </div>
          <RoleGate allow={['monitor', 'admin']} fallback={null}>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-deep-purple"
            >
              <Plus className="h-4 w-4" />
              New visit
            </button>
          </RoleGate>
        </div>
      </div>

      {visits.isError && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Failed to load visits: {(visits.error as Error).message}
        </div>
      )}

      {!visits.isLoading && totalCount === 0 && (
        <EmptyState
          icon={Calendar}
          title="No visits for this day"
          description={
            filterUser
              ? 'Try a different worker or date, or schedule a new visit.'
              : 'Schedule a visit manually, or connect AlayaCare / ShiftCare in Settings → Roster integrations to sync them automatically.'
          }
        />
      )}

      {grouped.length > 0 && (
        <div className="flex flex-col gap-4">
          {grouped.map((group) => {
            const dailyKm = group.visits.reduce(
              (sum, v) => sum + (v.travel_km_from_previous ?? 0),
              0,
            );
            return (
              <div key={group.name} className="panel">
                <div className="flex items-center gap-2 border-b border-line bg-surface-3 px-4 py-2 text-sm font-semibold text-ink">
                  <UserIcon className="h-4 w-4 text-brand-purple" />
                  {group.name}
                  <span className="text-xs font-normal text-ink-faint">
                    · {group.visits.length} visit{group.visits.length === 1 ? '' : 's'}
                  </span>
                  {dailyKm > 0 && (
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                      <Navigation className="h-3 w-3" />
                      {dailyKm.toFixed(1)} km travel
                    </span>
                  )}
                </div>
                <ul className="divide-y divide-line">
                  {group.visits.map((v) => (
                    <Fragment key={v.id}>
                      {v.travel_km_from_previous != null && (
                        <TravelLegRow
                          km={v.travel_km_from_previous}
                          durationS={v.travel_duration_s_from_previous}
                        />
                      )}
                      <VisitRow visit={v} onEdit={() => setEditing(v)} />
                    </Fragment>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <VisitForm mode="create" onClose={() => setShowCreate(false)} initialDate={date} />
      )}
      {editing && <VisitForm mode="edit" existing={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TravelLegRow({ km, durationS }: { km: number; durationS: number | null }) {
  return (
    <li className="flex items-center gap-2 bg-indigo-50/40 px-4 py-1.5 text-[11px] text-indigo-700">
      <Navigation className="h-3 w-3" />
      <span className="font-medium">{km.toFixed(1)} km</span>
      {typeof durationS === 'number' && durationS > 0 && (
        <span className="text-indigo-500">· ~{Math.round(durationS / 60)} min</span>
      )}
      <span className="text-indigo-400">travel to next visit</span>
    </li>
  );
}

function VisitRow({ visit, onEdit }: { visit: VisitDto; onEdit: () => void }) {
  const update = useUpdateVisit();
  const del = useDeleteVisit();
  const isExternal = visit.provider !== 'manual';

  // Safety signal: a scheduled visit whose start has passed with no arrival —
  // the worker is late or a no-show risk. And once they arrive, how far off the
  // scheduled time they were.
  const startMs = new Date(visit.scheduled_start).getTime();
  const isLate = visit.status === 'scheduled' && startMs < Date.now();
  const lateMin = isLate ? Math.floor((Date.now() - startMs) / 60_000) : 0;
  const arrivalDeltaMin = visit.actual_arrival
    ? Math.round((new Date(visit.actual_arrival).getTime() - startMs) / 60_000)
    : null;

  async function setStatus(next: VisitStatus) {
    if (next === visit.status) return;
    const patch: Parameters<typeof update.mutate>[0] = { id: visit.id, status: next };
    if (next === 'arrived' && !visit.actual_arrival)
      patch.actual_arrival = new Date().toISOString();
    if (next === 'completed' && !visit.actual_departure)
      patch.actual_departure = new Date().toISOString();
    await update.mutateAsync(patch);
  }

  return (
    <li
      className={cn(
        'grid grid-cols-[64px_1fr_auto] items-start gap-3 px-4 py-3 transition hover:bg-surface-2',
        isLate && 'bg-red-50/40',
      )}
    >
      {/* Time block — mono tabular numerals matching the design's m-visit row */}
      <div className="flex flex-col items-center justify-center text-center">
        <span className="font-mono text-[13px] font-bold text-ink">
          {formatTimeShort(visit.scheduled_start)}
        </span>
        <span className="font-mono text-[10px] text-ink-mute">
          → {formatTimeShort(visit.scheduled_end)}
        </span>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-heading text-[15px] font-bold text-ink">
            {visit.client_display_name ?? 'No client'}
          </span>
          {visit.service_type && (
            <span className="font-mono text-[10.5px] uppercase tracking-eyebrow-tight text-ink-faint">
              {visit.service_type}
            </span>
          )}
          <span className={cn('pill', STATUS_CHIPS[visit.status])}>
            {visit.status.replace('_', ' ')}
          </span>
          {isLate && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 ring-1 ring-inset ring-red-200">
              <Clock className="h-2.5 w-2.5" />
              {lateMin < 60
                ? `${lateMin}m late`
                : `${Math.floor(lateMin / 60)}h ${lateMin % 60}m late`}
            </span>
          )}
          {isExternal && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-info-soft px-2 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-eyebrow-tight text-brand-purple ring-1 ring-inset ring-brand-purple/20"
              title={`Synced from ${visit.provider}`}
            >
              <Plug className="h-2.5 w-2.5" />
              {visit.provider}
            </span>
          )}
        </div>
        {visit.location_address && (
          <div className="mt-1 flex items-center gap-1 font-mono text-[10.5px] text-ink-mute">
            <MapPin className="h-3 w-3" />
            {visit.location_address}
          </div>
        )}
        {(visit.actual_arrival || visit.actual_departure) && (
          <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-ink-faint">
            {visit.actual_arrival && (
              <span>
                arrived {formatTimeShort(visit.actual_arrival)}
                {arrivalDeltaMin != null && Math.abs(arrivalDeltaMin) >= 5 && (
                  <span className={arrivalDeltaMin > 0 ? 'text-red-600' : 'text-emerald-600'}>
                    {' '}
                    (
                    {arrivalDeltaMin > 0
                      ? `${arrivalDeltaMin}m late`
                      : `${-arrivalDeltaMin}m early`}
                    )
                  </span>
                )}
              </span>
            )}
            {visit.actual_departure && <span>left {formatTimeShort(visit.actual_departure)}</span>}
          </div>
        )}
        {visit.notes && (
          <div className="mt-1.5 rounded-md bg-surface-tinted px-2 py-1 text-[11px] text-ink-dim">
            {visit.notes}
          </div>
        )}
      </div>
      <RoleGate allow={['monitor', 'admin']} fallback={null}>
        {/*
         * Roster-sourced visits (provider != 'manual') hide every mutation
         * control. The upstream roster system is the source of truth for the
         * whole record — status, arrival, departure, schedule, client, notes.
         * Local edits would just diverge until the next sync overwrote them.
         * The API enforces the same rule with 409 roster_visit_read_only on
         * both PATCH and DELETE, so this is defence-in-depth, not a styling-
         * only gate. Admins manage these visits upstream; SQS / REST brings
         * the changes back.
         */}
        {!isExternal && (
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-1">
              {visit.status === 'scheduled' && (
                <button
                  type="button"
                  onClick={() => setStatus('arrived')}
                  disabled={update.isPending}
                  className="rounded px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-40"
                >
                  Mark arrived
                </button>
              )}
              {(visit.status === 'scheduled' || visit.status === 'arrived') && (
                <button
                  type="button"
                  onClick={() => setStatus('completed')}
                  disabled={update.isPending}
                  className="rounded px-2 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                >
                  Complete
                </button>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={onEdit}
                className="rounded p-1 text-ink-faint hover:bg-surface-3 hover:text-ink"
                title="Edit"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </button>
              <RoleGate allow={['admin']} fallback={null}>
                <button
                  type="button"
                  disabled={del.isPending}
                  onClick={() => {
                    if (window.confirm('Delete this visit?')) del.mutate(visit.id);
                  }}
                  className="rounded p-1 text-red-600 hover:bg-red-50 disabled:opacity-40"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </RoleGate>
            </div>
          </div>
        )}
      </RoleGate>
    </li>
  );
}

function VisitForm({
  mode,
  existing,
  onClose,
  initialDate,
}: {
  mode: 'create' | 'edit';
  existing?: VisitDto;
  onClose: () => void;
  initialDate?: string;
}) {
  const roster = useRoster();
  const create = useCreateVisit();
  const update = useUpdateVisit();
  const mut = mode === 'create' ? create : update;

  const [userId, setUserId] = useState(existing?.user_id ?? '');
  const [clientName, setClientName] = useState(existing?.client_display_name ?? '');
  const [serviceType, setServiceType] = useState(existing?.service_type ?? '');
  const [address, setAddress] = useState(existing?.location_address ?? '');
  const [start, setStart] = useState(() =>
    existing ? toDateTimeInput(existing.scheduled_start) : defaultStart(initialDate),
  );
  const [end, setEnd] = useState(() =>
    existing ? toDateTimeInput(existing.scheduled_end) : defaultEnd(initialDate),
  );
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const workers = (roster.data?.members ?? []).filter((m) => m.app_role === 'worker');
  const canSubmit = userId && start && end;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const payload: CreateVisitInput = {
      user_id: userId,
      scheduled_start: new Date(start).toISOString(),
      scheduled_end: new Date(end).toISOString(),
      client_display_name: clientName.trim() || null,
      service_type: serviceType.trim() || null,
      location_address: address.trim() || null,
      notes: notes.trim() || null,
    };
    try {
      if (mode === 'create') {
        await create.mutateAsync(payload);
      } else if (existing) {
        await update.mutateAsync({ id: existing.id, ...payload });
      }
      onClose();
    } catch {
      /* surfaced below */
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className="w-full max-w-xl rounded-lg bg-white p-5 shadow-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="visit-title" className="text-base font-semibold text-ink">
              {mode === 'create' ? 'Schedule visit' : 'Edit visit'}
            </h2>
            <p className="mt-0.5 text-xs text-ink-faint">
              {existing?.provider && existing.provider !== 'manual' ? (
                <>
                  Synced from <strong>{existing.provider}</strong>. Changes made here won't
                  propagate back — adjust upstream for roster-sourced visits.
                </>
              ) : (
                'Manual visit — not linked to any external roster system.'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-mute hover:bg-surface-3 hover:text-ink-dim"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Worker</span>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            >
              <option value="" disabled>
                Select worker
              </option>
              {workers.map((w) => (
                <option key={w.user_id} value={w.user_id}>
                  {w.display_name}
                </option>
              ))}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Scheduled start</span>
              <input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                required
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Scheduled end</span>
              <input
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                required
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Client name</span>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                maxLength={200}
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-dim">Service type</span>
              <input
                type="text"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                placeholder="Personal care, Clinical visit…"
                maxLength={200}
                className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Address</span>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="4 Pitt Street, Sydney NSW"
              maxLength={500}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="Gate code, pet reminders, parking tips…"
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
            />
          </label>

          {mut.isError && (
            <div className="inline-flex items-start gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-800">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              {(mut.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line bg-white px-3 py-1.5 text-sm font-medium text-ink-dim hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || mut.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              {mut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              {mode === 'create' ? 'Schedule' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatTimeShort(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toDateTimeInput(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${hh}:${mm}`;
}

function defaultStart(date?: string): string {
  const base = date ?? toDateInput(new Date());
  return `${base}T09:00`;
}

function defaultEnd(date?: string): string {
  const base = date ?? toDateInput(new Date());
  return `${base}T10:00`;
}

// Unused silencer — component link surface grows later.
void Link;
void formatDateTime;
